/**
 * ReferencesTab — the published literature behind a run, packaged for EndNote.
 *
 * Every source the drafters were shown by `search_literature` (PubMed,
 * ClinicalTrials.gov, bioRxiv), de-duplicated server-side, cited ones first.
 * Drafted sections already carry plain-text EndNote temporary citations
 * (`{Surname, Year, title words}`); this tab is the writer's half of the
 * round trip:
 *
 *   Export .ris        — download the references and import them into EndNote
 *                        (File → Import, or double-click). Re-importing is safe.
 *   Insert at cursor   — put a reference's placeholder where the writer's cursor
 *                        is. The text comes from the server, never composed here,
 *                        so EndNote matches it to the imported record.
 *
 * Then Word's EndNote tab → "Update Citations and Bibliography" formats every
 * placeholder in the writer's chosen style. Asthra never formats citations.
 *
 * Data: GET /runs/{runId}/references (`?module_id=` for "This section").
 */

import { Button, Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  type RunReference,
  type RunReferencesResponse,
  downloadBlob,
  fetchRunReferencesRis,
  getRunReferences,
} from "../../../api/client";
import { markCursorSectionChanged } from "../../../services/syncEngine";
import { enqueueWordWrite } from "../../../services/wordWriteQueue";
import { insertPlainTextAtSelection } from "../../../services/wordWriter";
import type { SpineSection } from "../../hooks/useVariantCRun";
import { brandColors, fonts, inkColors, statusColors, surfaceColors, typeScale } from "../../tokens";

type Scope = "section" | "all";

// ---------------------------------------------------------------------------
// Pure helpers — exported for tests
// ---------------------------------------------------------------------------

/** "Cited in §2.1, §4.2" / "Found while drafting — not cited". */
export function citedLabel(ref: Pick<RunReference, "cited_in_sections">): string {
  const secs = ref.cited_in_sections ?? [];
  return secs.length ? `Cited in ${secs.map((s) => `§${s}`).join(", ")}` : "Found while drafting — not cited";
}

/** Link to the record: URL, else PubMed / registry / DOI. */
export function referenceHref(ref: Pick<RunReference, "url" | "pmid" | "nct_id" | "doi">): string | null {
  if (ref.url) return ref.url;
  if (ref.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${ref.pmid}/`;
  if (ref.nct_id) return `https://clinicaltrials.gov/study/${ref.nct_id}`;
  if (ref.doi) return `https://doi.org/${ref.doi}`;
  return null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: { padding: "12px 16px", overflowY: "auto", height: "100%", fontFamily: fonts.body },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    marginBottom: "6px",
  },
  scope: { display: "flex", columnGap: "4px" },
  scopeBtn: {
    fontSize: typeScale.code,
    fontFamily: fonts.body,
    padding: "2px 8px",
    borderRadius: "10px",
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: surfaceColors.line,
    borderRightColor: surfaceColors.line,
    borderBottomColor: surfaceColors.line,
    borderLeftColor: surfaceColors.line,
    backgroundColor: "transparent",
    color: inkColors.ink2,
    cursor: "pointer",
  },
  scopeBtnActive: {
    backgroundColor: brandColors.light,
    color: brandColors.primary,
    borderTopColor: brandColors.mid,
    borderRightColor: brandColors.mid,
    borderBottomColor: brandColors.mid,
    borderLeftColor: brandColors.mid,
  },
  help: { fontSize: typeScale.code, color: inkColors.ink3, marginBottom: "10px", lineHeight: 1.45 },
  card: {
    padding: "8px 0 10px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: surfaceColors.line,
  },
  title: { fontSize: typeScale.body, color: inkColors.ink, lineHeight: 1.4 },
  link: { color: brandColors.primary, textDecorationLine: "none" },
  meta: { fontSize: typeScale.code, color: inkColors.ink3, marginTop: "2px" },
  placeholder: {
    fontFamily: fonts.mono,
    fontSize: typeScale.code,
    color: inkColors.ink2,
    marginTop: "4px",
    wordBreak: "break-word",
  },
  cited: { color: statusColors.cool },
  actions: { marginTop: "6px", display: "flex", columnGap: "8px", alignItems: "center" },
  feedback: { fontSize: typeScale.code, color: inkColors.ink3 },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "8px",
    padding: "32px 16px",
    color: inkColors.ink3,
    fontSize: typeScale.body,
    textAlign: "center",
  },
  errorText: { color: statusColors.danger },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ReferencesTabProps {
  section: SpineSection;
  runId: string;
}

const ReferencesTab: React.FC<ReferencesTabProps> = ({ section, runId }) => {
  const styles = useStyles();
  const [scope, setScope] = useState<Scope>("section");
  const [payload, setPayload] = useState<RunReferencesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [feedback, setFeedback] = useState<{ key: string; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRunReferences(runId, scope === "section" ? section.id : undefined)
      .then((resp) => {
        if (!cancelled) {
          setPayload(resp);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, scope]);

  const onExport = useCallback(async () => {
    setExporting(true);
    try {
      const { blob, filename } = await fetchRunReferencesRis(runId);
      downloadBlob(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }, [runId]);

  const onInsert = useCallback(async (ref: RunReference) => {
    const ok = await enqueueWordWrite(
      () => insertPlainTextAtSelection(ref.placeholder),
      "Inserting the reference",
    ).catch(() => false);
    // Our own insert's Word events are ignored by the auto-save watcher — mark
    // the section so the placeholder reaches the saved draft.
    if (ok) markCursorSectionChanged();
    setFeedback({
      key: ref.citation_key,
      text: ok
        ? "Inserted — update citations in EndNote to format it."
        : "Couldn't insert — click into the document first.",
    });
  }, []);

  const scopeToggle = (
    <div className={styles.scope} role="tablist" aria-label="Reference scope">
      {(["section", "all"] as Scope[]).map((s) => (
        <button
          key={s}
          type="button"
          role="tab"
          aria-selected={scope === s}
          className={mergeClasses(styles.scopeBtn, scope === s && styles.scopeBtnActive)}
          onClick={() => setScope(s)}
        >
          {s === "section" ? "This section" : "Whole document"}
        </button>
      ))}
    </div>
  );

  if (loading && !payload) {
    return (
      <div className={styles.centered}>
        <Spinner size="tiny" />
        <span>Loading references…</span>
      </div>
    );
  }
  if (!payload) {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>Could not load references: {error}</span>
      </div>
    );
  }

  const refs = payload.references;
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {scopeToggle}
        <Button size="small" appearance="primary" onClick={onExport} disabled={exporting || payload.count === 0}>
          {exporting ? "Exporting…" : "Export .ris"}
        </Button>
      </div>
      <div className={styles.help}>
        Drafted text carries EndNote placeholders like {"{Smith, 2024, title words}"}. Export the references, import the
        .ris into your EndNote library, then use Update Citations and Bibliography in Word's EndNote tab to format them
        in your style.
      </div>
      {error ? <div className={mergeClasses(styles.help, styles.errorText)}>{error}</div> : null}
      {refs.length === 0 ? (
        <div className={styles.centered}>
          <span>No published literature {scope === "section" ? "for this section" : "in this run"} yet.</span>
          <span>
            References appear here when drafting uses a literature search (PubMed, ClinicalTrials.gov, bioRxiv).
          </span>
        </div>
      ) : (
        refs.map((ref) => {
          const href = referenceHref(ref);
          return (
            <div key={`${ref.citation_key}-${ref.doi ?? ref.pmid ?? ref.nct_id ?? ref.title}`} className={styles.card}>
              <div className={styles.title}>
                {href ? (
                  <a className={styles.link} href={href} target="_blank" rel="noreferrer">
                    {ref.formatted}
                  </a>
                ) : (
                  ref.formatted
                )}
              </div>
              <div className={mergeClasses(styles.meta, ref.cited_in_sections.length > 0 && styles.cited)}>
                {citedLabel(ref)}
              </div>
              <div className={styles.placeholder}>{ref.placeholder}</div>
              <div className={styles.actions}>
                <Button size="small" appearance="secondary" onClick={() => void onInsert(ref)}>
                  Insert at cursor
                </Button>
                {feedback?.key === ref.citation_key ? <span className={styles.feedback}>{feedback.text}</span> : null}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ReferencesTab;
